import { AidKitLogo } from '@aidkitorg/component-library';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { get_deployment, useAPI, usePost } from '../API';
import { PublicConfigurationContext, SupportedLanguage } from '../Context';

const AIDKIT_LOGO = <AidKitLogo />

const ThemeContext = createContext<ThemeContextType>({
  logo: AIDKIT_LOGO,
  languages: ['en', 'es'],
});

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    // TODO: should we throw an error here or just silently fail?
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

type ThemeProviderProps = {
  children: React.ReactNode
}  

type ThemeContextType = {
  logo: React.ReactElement,
  languages: Array<SupportedLanguage>
}

// TODO: make it so that the sizing can be changed

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [logo, setLogo] = useState<React.ReactElement>(AIDKIT_LOGO)
  const [languages, setLanguages] = useState<Array<SupportedLanguage>>(['en', 'es'])
  
  // Use existing contexts for some data
  const publicConfig = useContext(PublicConfigurationContext);
  // Same config as in DatabaselessAppConfiguration
  const getConfig = usePost("/program/configuration");

  // Set logo from public config context
  useEffect(() => {
    if (publicConfig?.interface?.applicantFacingLogo?.url) {
      const customLogo = <img
        className="w-auto"
        src={publicConfig.interface.applicantFacingLogo.url}
        alt={publicConfig.name || 'AidKit Program'}
      />
      setLogo(customLogo)
    } else {
      // Reset to default logo if no custom logo
      setLogo(AIDKIT_LOGO)
    }
  }, [publicConfig])

  useEffect(() => {
    (async () => {
      try {
        // Add program parameter like DatabaselessAppConfiguration does
        const config = await getConfig({ program: get_deployment() });
        
        if (config && Object.keys(config).length > 0) {
          // Handle both language formats:
          let languageList: Array<SupportedLanguage> = [];
          
          if (typeof config.languages === 'string') {
            // Handle comma-separated string format
            languageList = config.languages.split(',')
              .map(lang => lang.trim() as SupportedLanguage);
          } else if (Array.isArray(config.languages)) {
            // Handle array format
            languageList = config.languages;
          } else if (config.languages) {
            languageList = [config.languages];
          }
          
          if (languageList.length > 0) {
            setLanguages([...languageList]);
          } else {
            // Config loaded but no languages - use fallback
            setLanguages(['en', 'es']);
          }
        }
      } catch (error) {
        console.error('Error fetching language configuration:', error);
        // Keep default languages on error
      }
    })();
  }, [])
	
  const value: ThemeContextType = {
    logo,
    languages
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}